import { useState, useEffect } from 'react';
import api from '../api';
import { motion } from 'framer-motion';
import { 
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend, BarChart, Bar
} from 'recharts';
import { TrendingUp, Target, Loader2, Info, Wallet, DollarSign, ArrowUpRight, Zap } from 'lucide-react';

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get('/analytics');
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: 'var(--primary)' }}>
        <Loader2 size={48} style={{ animation: 'spin 2s linear infinite' }} />
      </div>
    );
  }

  if (!data) return <div>Failed to load analytics</div>;

  const { 
    balanceDistribution, cashFlowTrend, targetForecasts,
    totalMonthlyIncome, totalMonthlyAllocation, allocationEfficiency,
    paycheckBreakdown, netWorthProjection 
  } = data;

  const totalAssets = balanceDistribution.reduce((acc, curr) => acc + curr.value, 0);

  const getInsights = () => {
    let insights = [];
    if (allocationEfficiency >= 20) {
      insights.push(`Strong savings rate of ${allocationEfficiency.toFixed(1)}%. You are aggressively building wealth.`);
    } else if (allocationEfficiency > 0) {
      insights.push(`Your savings rate is ${allocationEfficiency.toFixed(1)}%. Pushing this above 20% will accelerate your financial independence.`);
    }
    
    const funded = targetForecasts.filter(f => f.current >= f.target);
    if (funded.length > 0) {
      insights.push(`You have ${funded.length} fully funded target buckets!`);
    }

    const compounding = targetForecasts.filter(f => f.apy && f.apy > 0);
    if (compounding.length > 0) {
      const avgApy = compounding.reduce((s, f) => s + f.apy, 0) / compounding.length;
      insights.push(`Your yield-bearing buckets average ${avgApy.toFixed(2)}% APY. Compound interest is accelerating your timeline.`);
    }
    
    const fastestTarget = targetForecasts.filter(f => f.monthsRemaining !== null && f.current < f.target).sort((a, b) => a.monthsRemaining - b.monthsRemaining)[0];
    if (fastestTarget) {
      insights.push(`At your current velocity, your ${fastestTarget.name} bucket will be fully funded in ${fastestTarget.monthsRemaining} months.`);
    }
    
    return insights.length > 0 ? insights : ["Keep allocating your paychecks to build up history for more insights."];
  };

  const insights = getInsights();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="show" style={{ paddingBottom: '2rem' }}>
      <motion.header variants={itemVariants} style={{ marginBottom: '2rem' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <TrendingUp className="text-primary" size={28} /> Analytics & Forecasting
        </h2>
        <p className="text-muted" style={{ margin: 0, fontSize: '1.1rem' }}>
          Track your wealth distribution and automatically forecast your financial goals.
        </p>
      </motion.header>

      {/* Algorithmic Insights */}
      <motion.div variants={itemVariants} className="glass" style={{ padding: '1.5rem', marginBottom: '1.5rem', borderLeft: '4px solid var(--primary)' }}>
        <h3 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary)' }}>
          <Zap size={20} /> Algorithmic Insights
        </h3>
        <ul style={{ margin: 0, paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', color: 'var(--text)' }}>
          {insights.map((insight, idx) => (
            <li key={idx} style={{ lineHeight: 1.5 }}>{insight}</li>
          ))}
        </ul>
      </motion.div>

      {/* KPI Row */}
      <motion.div variants={itemVariants} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Wallet size={16} /> Total Managed Assets</span>
          <h3 style={{ margin: 0, fontSize: '2rem', color: 'var(--text)' }}>${totalAssets.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
        </div>
        <div className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><DollarSign size={16} /> Monthly Income</span>
          <h3 style={{ margin: 0, fontSize: '2rem', color: 'var(--success)' }}>${(totalMonthlyIncome || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
        </div>
        <div className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Target size={16} /> Monthly Allocation</span>
          <h3 style={{ margin: 0, fontSize: '2rem', color: 'var(--primary)' }}>${(totalMonthlyAllocation || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
        </div>
        <div className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><ArrowUpRight size={16} /> Savings Rate</span>
          <h3 style={{ margin: 0, fontSize: '2rem', color: 'var(--text)' }}>{(allocationEfficiency || 0).toFixed(1)}%</h3>
        </div>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        
        {/* Projected Net Worth */}
        <motion.div variants={itemVariants} className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>12-Month Net Worth Projection</h4>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={netWorthProjection} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorPrincipal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="var(--primary)" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorInterest" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--success)" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="var(--success)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val}`} />
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <RechartsTooltip 
                  formatter={(value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2})}`}
                  contentStyle={{ backgroundColor: 'rgba(20,24,30,0.9)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
                />
                <Legend verticalAlign="top" height={36}/>
                <Area type="monotone" dataKey="Principal" stackId="1" stroke="var(--primary)" strokeWidth={2} fillOpacity={1} fill="url(#colorPrincipal)" />
                <Area type="monotone" dataKey="Interest" stackId="1" stroke="var(--success)" strokeWidth={2} fillOpacity={1} fill="url(#colorInterest)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            * Projections are estimates based on your current allocation rules and APY settings, not guaranteed returns.
          </div>
        </motion.div>

        {/* Paycheck Efficiency */}
        <motion.div variants={itemVariants} className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Paycheck Breakdown</h4>
          {(!paycheckBreakdown || paycheckBreakdown.length === 0) ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              No income sources configured.
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={paycheckBreakdown} layout="vertical" margin={{ top: 10, right: 30, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={true} vertical={false} />
                  <XAxis type="number" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} width={100} />
                  <RechartsTooltip 
                    formatter={(value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2})}`}
                    contentStyle={{ backgroundColor: 'rgba(20,24,30,0.9)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
                    cursor={{fill: 'rgba(255,255,255,0.05)'}}
                  />
                  <Legend verticalAlign="top" height={36}/>
                  <Bar dataKey="Allocated" stackId="a" fill="var(--primary)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="SafeToSpend" stackId="a" fill="var(--success)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        
        {/* Net Worth Distribution */}
        <motion.div variants={itemVariants} className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Current Distribution</h4>
          {balanceDistribution.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              No funds have balances yet.
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={balanceDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {balanceDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2})}`}
                    contentStyle={{ backgroundColor: 'rgba(20,24,30,0.9)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>

        {/* Cash Flow Trend */}
        <motion.div variants={itemVariants} className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Cash Flow Trend (Last 6 Months)</h4>
          {cashFlowTrend.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              Not enough history yet to generate trend line.
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cashFlowTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorDeposited" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorAllocated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--success)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--success)" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val}`} />
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <RechartsTooltip 
                    formatter={(value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2})}`}
                    contentStyle={{ backgroundColor: 'rgba(20,24,30,0.9)', border: '1px solid var(--surface-border)', borderRadius: '8px' }}
                  />
                  <Legend verticalAlign="top" height={36}/>
                  <Area type="monotone" dataKey="Deposited" stroke="var(--primary)" fillOpacity={1} fill="url(#colorDeposited)" />
                  <Area type="monotone" dataKey="Allocated" stroke="var(--success)" fillOpacity={1} fill="url(#colorAllocated)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>
      </div>

      {/* Target Forecasting */}
      <motion.div variants={itemVariants} className="glass" style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Target className="text-primary" size={20} /> Goal Forecasting
        </h3>
        
        {targetForecasts.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
            You haven't set any Target Caps on your buckets yet. Go to "My Funds" to set targets!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {targetForecasts.map(fund => (
              <div key={fund.id} style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--surface-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: fund.color }}></div>
                    <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fund.name}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>${fund.current.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    <span style={{ color: 'var(--text-muted)' }}> / ${fund.target.toLocaleString()}</span>
                  </div>
                </div>

                <div style={{ width: '100%', height: '12px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', overflow: 'hidden', marginBottom: '0.75rem' }}>
                  <div 
                    style={{ 
                      width: `${fund.progressPercent}%`, 
                      height: '100%', 
                      background: fund.color,
                      transition: 'width 1s ease-in-out'
                    }}
                  ></div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Info size={14} /> 
                    Velocity: ${fund.velocity.toLocaleString(undefined, {minimumFractionDigits: 2})}/mo 
                    ({fund.velocityType}) {fund.apy > 0 && `• ${fund.apy.toFixed(2)}% APY`}
                  </span>
                  {fund.current >= fund.target ? (
                    <span style={{ color: 'var(--success)', fontWeight: 600 }}>Fully Funded! 🎉</span>
                  ) : fund.monthsRemaining !== null ? (
                    <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Est. Goal: {fund.monthsRemaining} months</span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>Not enough data to forecast</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

    </motion.div>
  );
}
